import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, Badge, Button, Table, 
  Modal, Alert, Spinner, Tabs, Tab
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock, faCheck, faTimes, faEye, faVideo,
  faCalendar, faDollarSign, faCoins, faUserMd,
  faCommentDots, faUser, faUserMd as faTherapist
} from '@fortawesome/free-solid-svg-icons';
import Header from '../components/header';
import Footer from '../components/footer';
import TherapyChat from '../components/TherapyChat';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';

// Helper function to safely parse dates for Safari compatibility
const parseDate = (dateString) => {
  if (!dateString) return null;
  
  // Replace space with 'T' for ISO format compatibility
  const isoString = dateString.replace(' ', 'T');
  
  try {
    return new Date(isoString);
  } catch (error) {
    console.error('Error parsing date:', dateString, error);
    return null;
  }
};

const MyTherapySessions = () => {
  const { user } = useContext(MyContext);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);


  useEffect(() => {
    if (user) {
      loadMySessions();
    }
  }, [user]);

 
  const loadMySessions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${backendUrl}/fetch_my_therapy_sessions.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.user_id })
      });

      const responseText = await response.text();
      //console.log('My sessions raw response:', responseText);

      try {
        const data = JSON.parse(responseText);
        //console.log('My sessions parsed response:', data);

        if (data.success) {
          setSessions(data.sessions);
        } else {
          setError(data.messages || 'Failed to load sessions');
        }
      } catch (jsonErr) {
        console.error('Failed to parse sessions response as JSON:', jsonErr);
        //console.error('Raw response was:', responseText);
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Failed to load sessions: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSessionStatusBadge = (status) => {
    const variants = {
      'pending': 'warning',
      'hold': 'info',
      'active': 'success',
      'completed': 'info',
      'cancelled': 'danger',
      'closed': 'secondary',
      'paid': 'success'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getPaymentStatusBadge = (status) => {
    const variants = {
      'pending': 'warning',
      'paid': 'success',
      'cancelled': 'danger',
      'completed': 'info'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const handleOpenChat = (session) => {
    setSelectedSession(session);
    setShowChatModal(true);
  };

  const handleCloseChat = () => {
    setShowChatModal(false);
    setSelectedSession(null);
    // Don't automatically refresh sessions to avoid page reload issues
    // Sessions will be refreshed when user navigates or manually refreshes
  };

  const formatCoinsWithNaira = (coins) => {
    const naira = (coins || 0) * 100;
    return (
      <span>
        <Badge bg="warning" className="me-2">
          <FontAwesomeIcon icon={faCoins} className="me-1" />
          {coins?.toLocaleString() || '0'} coins
        </Badge>
        <small className="text-muted">
          (₦{naira.toLocaleString()})
        </small>
      </span>
    );
  };

  const formatCoins = (coins) => {
    return (
      <Badge bg="warning">
        <FontAwesomeIcon icon={faCoins} className="me-1" />
        {coins?.toLocaleString() || '0'} coins
      </Badge>
    );
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading your sessions...</p>
        </Container>
        <Footer />
      </>
    );
  }

  // Filter sessions based on user role
  const clientSessions = sessions.filter(s => s.user_id === user.user_id);

  const pendingSessions = clientSessions.filter(s => s.status === 'pending' || s.status === 'hold');
    
  const activeSessions = clientSessions.filter(s => s.status === 'active');
    
  const completedSessions = clientSessions.filter(s => s.status === 'completed' || s.status === 'closed');
    
  const closedSessions = clientSessions.filter(s => s.status === 'closed');

  const allSessions = clientSessions;

  return (
    <>
      <Header />
      
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>
              <FontAwesomeIcon icon={faCalendar} className="me-2 text-primary" />
              My Therapy Sessions
            </h2>
          
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FontAwesomeIcon icon={faClock} size="2x" className="text-warning mb-2" />
                <h4>{pendingSessions.length}</h4>
                <p className="text-muted">
                  {'Pending'}
                </p>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FontAwesomeIcon icon={faVideo} size="2x" className="text-success mb-2" />
                <h4>{activeSessions.length}</h4>
                <p className="text-muted">Active</p>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FontAwesomeIcon icon={faCheck} size="2x" className="text-info mb-2" />
                <h4>{completedSessions.length}</h4>
                <p className="text-muted">Completed</p>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <FontAwesomeIcon icon={faCoins} size="2x" className="text-primary mb-2" />
                <h4>{allSessions.reduce((total, s) => total + (parseFloat(s.coins_paid)  || 0), 0).toLocaleString()}</h4>
                <p className="text-muted">
                  {'Total Spent'}
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
          <Tab eventKey="all" title="All Sessions">
            <Card>
              <Card.Header>
                <h5 className="mb-0">All Therapy Sessions</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>{'Therapist'}</th>
                      <th>Issue</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allSessions.map((session) => (
                      <tr key={session.session_id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="me-2">
                              <FontAwesomeIcon 
                                icon={ faUserMd} 
                                className="text-primary" 
                              />
                            </div>
                            <div>
                              <strong>
                                {session.therapist_name}
                              </strong>
                              <br />
                              <small className="text-muted">
                                {session.therapist_email}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="text-truncate" style={{ maxWidth: '200px' }}>
                            {session.issue_text}
                          </div>
                        </td>
                        <td>{getSessionStatusBadge(session.status)}</td>
                        <td>{formatCoins(session.coins_paid)}</td>
                        <td>{parseDate(session.created_at)?.toLocaleDateString() || 'Invalid date'}</td>
                        <td>
                          <div className="d-flex gap-1">
                            {(session.status === 'active' || session.status === 'closed') && (
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => handleOpenChat(session)}
                              >
                                <FontAwesomeIcon icon={faCommentDots} className="me-1" />
                                Chat
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => {
                                setSelectedSession(session);
                                setShowSessionModal(true);
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} className="me-1" />
                              Details
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="pending" title={
            <span>
              Pending
              {pendingSessions.length > 0 && (
                <Badge bg="warning" text="dark" pill className="ms-2">
                  {pendingSessions.length}
                </Badge>
              )}
            </span>
          }>
            <Card>
              <Card.Header>
                <h5 className="mb-0">
                  {'Pending Sessions'}
                </h5>
              </Card.Header>
              <Card.Body>
                {pendingSessions.length === 0 ? (
                  <p className="text-muted">No pending sessions</p>
                ) : (
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>{'Therapist'}</th>
                        <th>Issue</th>
                        <th>Requested</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingSessions.map((session) => (
                        <tr key={session.session_id}>
                          <td>
                            {session.therapist_name}
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '200px' }}>
                              {session.issue_text}
                            </div>
                          </td>
                          <td>{parseDate(session.created_at)?.toLocaleDateString() || 'Invalid date'}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => {
                                setSelectedSession(session);
                                setShowSessionModal(true);
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} className="me-1" />
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="active" title={
            <span>
              Active
              {activeSessions.length > 0 && (
                <Badge bg="success" pill className="ms-2">
                  {activeSessions.length}
                </Badge>
              )}
            </span>
          }>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Active Sessions</h5>
              </Card.Header>
              <Card.Body>
                {activeSessions.length === 0 ? (
                  <p className="text-muted">No active sessions</p>
                ) : (
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>{'Therapist'}</th>
                        <th>Issue</th>
                        <th>Started</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeSessions.map((session) => (
                        <tr key={session.session_id}>
                          <td>
                            {session.therapist_name}
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '200px' }}>
                              {session.issue_text}
                            </div>
                          </td>
                          <td>{parseDate(session.updated_at)?.toLocaleDateString() || 'Invalid date'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleOpenChat(session)}
                              >
                                <FontAwesomeIcon icon={faCommentDots} className="me-1" />
                                Open Chat
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => {
                                  setSelectedSession(session);
                                  setShowSessionModal(true);
                                }}
                              >
                                <FontAwesomeIcon icon={faEye} className="me-1" />
                                Details
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="completed" title={
            <span>
              Completed
              {completedSessions.length > 0 && (
                <Badge bg="info" pill className="ms-2">
                  {completedSessions.length}
                </Badge>
              )}
            </span>
          }>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Completed & Closed Sessions</h5>
              </Card.Header>
              <Card.Body>
                {completedSessions.length === 0 ? (
                  <p className="text-muted">No completed sessions</p>
                ) : (
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>{'Therapist'}</th>
                        <th>Issue</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completedSessions.map((session) => (
                        <tr key={session.session_id}>
                          <td>
                            {session.therapist_name}
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '200px' }}>
                              {session.issue_text}
                            </div>
                          </td>
                          <td>{getSessionStatusBadge(session.status)}</td>
                          <td>{parseDate(session.updated_at)?.toLocaleDateString() || 'Invalid date'}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleOpenChat(session)}
                              >
                                <FontAwesomeIcon icon={faCommentDots} className="me-1" />
                                View Chat
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => {
                                  setSelectedSession(session);
                                  setShowSessionModal(true);
                                }}
                              >
                                <FontAwesomeIcon icon={faEye} className="me-1" />
                                Details
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>

      {/* Session Detail Modal */}
      <Modal show={showSessionModal} onHide={() => setShowSessionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Session Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSession && (
            <div>
              <Row>
                <Col md={6}>
                  <h6>Session Information</h6>
                  <p><strong>Requested:</strong> {parseDate(selectedSession.created_at)?.toLocaleString() || 'Invalid date'}</p>
                  {selectedSession.scheduled_time && (
                    <p><strong>Scheduled:</strong> {parseDate(selectedSession.scheduled_time)?.toLocaleString() || 'Invalid date'}</p>
                  )}
                  <p>
                    <strong>Status:</strong> {getSessionStatusBadge(selectedSession.status)}
                  </p>
                </Col>
                <Col md={6}>
                  <h6>Payment Details</h6>
                  <p><strong>Amount:</strong> {formatCoins(selectedSession.coins_paid || 0)}</p>
                </Col>
              </Row>
              
              <hr />
              
              <h6>Issue Description</h6>
              <p>{selectedSession.issue_text}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSessionModal(false)}>
            Close
          </Button>
          {(selectedSession?.status === 'active' || selectedSession?.status === 'closed') && (
            <Button 
              variant="primary"
              onClick={() => {
                setShowSessionModal(false);
                handleOpenChat(selectedSession);
              }}
            >
              <FontAwesomeIcon icon={faCommentDots} className="me-1" />
              Open Chat
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Chat Modal */}
      <Modal 
        show={showChatModal} 
        onHide={handleCloseChat} 
        size="xl"
        dialogClassName="chat-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faCommentDots} className="me-2" />
            Therapy Session Chat
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedSession && (
            <TherapyChat
              session={selectedSession}
              userId={user.user_id}
              onClose={handleCloseChat}
              isTherapist={false}
            />
          )}
        </Modal.Body>
      </Modal>

      <Footer />
    </>
  );
};

export default MyTherapySessions; 